import { compliance } from "./lib/compliance";
import { citedPrintsDataset } from "./lib/citedPrintsDataset";
import {
  COMPARE_POINTS,
  FAQ,
  HOW_TO_STEPS,
  SEO_MODIFIED,
  SEO_TOPICS,
  SITE_DESCRIPTION,
  SITE_NAME,
  SITE_TITLE,
} from "./content";

const parent = import.meta.env.VITE_PARENT_ORIGIN || compliance.parent;
const broker = import.meta.env.VITE_BROKER_NAME || compliance.brand;

export function siteUrl(): string {
  const fromEnv = import.meta.env.VITE_SITE_URL?.replace(/\/$/, "");
  if (fromEnv) return fromEnv;
  if (typeof window !== "undefined") return window.location.origin;
  return "https://ratecheck.azurehomeloans.com.au";
}

export function applySeo(): void {
  const url = `${siteUrl()}/`;
  const origin = parent || url;
  const modified = SEO_MODIFIED;

  setMeta("name", "description", SITE_DESCRIPTION);
  setMeta("name", "robots", "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1");
  setMeta("name", "googlebot", "index,follow");
  setMeta("name", "author", `${compliance.representative}, ${broker}`);
  setMeta("name", "geo.region", "AU");
  setMeta("name", "geo.placename", "Australia");
  setMeta("property", "og:type", "website");
  setMeta("property", "og:locale", "en_AU");
  setMeta("property", "og:site_name", broker);
  setMeta("property", "og:title", SITE_TITLE);
  setMeta("property", "og:description", SITE_DESCRIPTION);
  setMeta("property", "og:url", url);
  setMeta("name", "twitter:card", "summary");
  setMeta("name", "twitter:title", SITE_TITLE);
  setMeta("name", "twitter:description", SITE_DESCRIPTION);

  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.appendChild(canonical);
  }
  canonical.href = url;

  setLink("alternate", url, "en-AU");
  setLink("alternate", url, "x-default");

  let llms = document.querySelector<HTMLLinkElement>('link[title="LLM information"]');
  if (!llms) {
    llms = document.createElement("link");
    llms.rel = "alternate";
    llms.type = "text/plain";
    llms.title = "LLM information";
    document.head.appendChild(llms);
  }
  llms.href = `${url}llms.txt`;

  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": ["Organization", "FinancialService"],
        "@id": `${origin}/#broker`,
        name: broker,
        legalName: broker,
        url: origin,
        telephone: compliance.phone,
        email: compliance.email,
        taxID: compliance.abn,
        areaServed: { "@type": "Country", name: "Australia" },
        serviceType: "Mortgage broking",
        knowsAbout: SEO_TOPICS,
        sameAs: [origin, url],
        employee: {
          "@type": "Person",
          name: compliance.representative,
          jobTitle: "Mortgage broker",
          identifier: `ACR ${compliance.crn}`,
        },
      },
      {
        "@type": "WebApplication",
        "@id": `${url}#app`,
        name: SITE_NAME,
        url,
        applicationCategory: "FinanceApplication",
        operatingSystem: "Any",
        inLanguage: "en-AU",
        isAccessibleForFree: true,
        description: SITE_DESCRIPTION,
        provider: { "@id": `${origin}/#broker` },
        offers: { "@type": "Offer", price: "0", priceCurrency: "AUD" },
        audience: { "@type": "Audience", geographicArea: { "@type": "Country", name: "Australia" } },
        potentialAction: {
          "@type": "UseAction",
          name: "Check your home loan rate",
          target: url,
        },
      },
      {
        "@type": "WebPage",
        "@id": `${url}#page`,
        url,
        name: SITE_TITLE,
        description: SITE_DESCRIPTION,
        inLanguage: "en-AU",
        dateModified: modified,
        isPartOf: { "@id": `${origin}/#broker` },
        about: [{ "@id": `${url}#term-rate-check` }, ...SEO_TOPICS],
        mainEntity: { "@id": `${url}#app` },
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: ["#page-title", ".mast-lead", "[data-seo-direct-answer]", "#faq-heading"],
        },
        citation: [
          {
            "@type": "WebPage",
            name: "Home loan refinancing",
            url: "https://moneysmart.gov.au/home-loans/home-loan-refinancing",
          },
          {
            "@type": "WebPage",
            name: "Switching home loans",
            url: "https://moneysmart.gov.au/home-loans/switching-home-loans",
          },
          {
            "@type": "WebPage",
            name: "First home buyers",
            url: "https://www.firsthomebuyers.gov.au/",
          },
        ],
        significantLink: [`${url}how-to-check-home-loan-rate.html`, `${url}llms.txt`],
      },
      citedPrintsDataset({ url, origin, broker, modified }),
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          "@type": "Question",
          name: item.q,
          acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
      },
      {
        "@type": "ItemList",
        "@id": `${url}#paths`,
        name: "Rate review or refinance",
        itemListElement: COMPARE_POINTS.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          description: item.text,
        })),
      },
      {
        "@type": "HowTo",
        "@id": `${url}#howto`,
        name: "How to check if you are overpaying on your home loan in Australia",
        description:
          "Compare your current home loan rate with eligible market products, then stay and negotiate or refinance.",
        totalTime: "PT2M",
        step: HOW_TO_STEPS.map((step, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: step.name,
          text: step.text,
        })),
      },
      {
        "@type": "DefinedTerm",
        "@id": `${url}#term-rate-check`,
        name: "home loan rate check",
        description:
          "A comparison of the interest rate on an existing Australian home loan statement with advertised new-customer variable prints a brokerage can place, at the same occupancy, repayment type and LVR. The yearly gap is an illustration, not an approval.",
        inDefinedTermSet: url,
        url,
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: broker,
            item: origin,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Home loan rate check",
            item: url,
          },
        ],
      },
    ],
  };

  let script = document.getElementById("jsonld-rate-check") as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement("script");
    script.id = "jsonld-rate-check";
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(graph);
}

function setLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    if (hreflang) el.hreflang = hreflang;
    document.head.appendChild(el);
  }
  el.href = href;
}

function setMeta(attr: "name" | "property", key: string, value: string) {
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = value;
}
